import type {AuthorityField008Definitions} from 'typings/portaro.be.types';
import type {Component} from 'typings/portaro.fe.types';
import type {ModalWindowActions, ModalWindowBindings, ModalWindowResolveValues} from '../../../../modals/modals.types';
import type {IOnDestroy, IOnInit, IScope} from 'angular';
import type {LogService} from 'core/logging/log.service';
import type {KpUniversalFormSettings} from 'shared/value-editors/kp-universal-form/types';
import type {ObjectValueEditorField} from 'shared/value-editors/internal/meta-editors/object/types';
import type {FormController} from 'shared/value-editors/internal/forms/types';
import type {AuthorityField8EditorServiceFactory} from './authority-field8-editor-service.factory';
import type {AuthorityField8Model} from '../utils/types';
import type {Subscription} from 'rxjs';
import type {AuthorityField8EditorService} from './authority-field8-editor.service';
import type ModalConversationService from '../../../../modals/modal-conversation.service';
import {createPayloadActionResponse} from '../../../../modals/modal-utils';
import {IncorrectSectionFormatError, UnknownSectionsCodesError} from './authority-field8-errors';
import KpUniversalForm from 'shared/value-editors/kp-universal-form/KpUniversalForm.svelte';

export interface AuthorityField8EditorModalModel {
    value: string;
    definitions: AuthorityField008Definitions;
}

export class AuthorityField8EditorModalComponent implements Component<ModalWindowBindings<AuthorityField8EditorModalModel>> {
    public static componentName = 'authorityField8EditorModal';

    public bindings = {
        resolve: '<'
    } as const;
    public controller = AuthorityField8EditorModalComponentController;
    public templateUrl = require('./authority-field8-editor-modal.component.tpl.ftl');
}

class AuthorityField8EditorModalComponentController implements ModalWindowBindings<AuthorityField8EditorModalModel>, IOnInit, IOnDestroy {
    public resolve: ModalWindowResolveValues<AuthorityField8EditorModalModel>;

    public kpUniversalFormComponent = KpUniversalForm;

    public modalWindowActions: ModalWindowActions;
    public model: AuthorityField8EditorModalModel;

    public formModel: AuthorityField8Model;
    public formSettings: KpUniversalFormSettings<AuthorityField8Model, ObjectValueEditorField<AuthorityField8Model>[]>; // form fields definition
    public isFormValid: boolean;

    private formController: FormController<AuthorityField8Model>;
    private subscription: Subscription;
    private editorService: AuthorityField8EditorService; // field 008 editor logic

    private definitions: AuthorityField008Definitions;
    private error: Error | null = null;

    /*@ngInject*/
    constructor(private authorityField8EditorServiceFactory: AuthorityField8EditorServiceFactory,
                private modalConversationService: ModalConversationService,
                private logService: LogService,
                private $scope: IScope) {
    }

    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    public async $onInit() {
        this.modalWindowActions = this.resolve.modalWindowActions;
        this.model = this.resolve.model;
        this.definitions = this.model.definitions;

        this.editorService = this.authorityField8EditorServiceFactory.createService(this.definitions);

        this.formSettings = this.editorService.initializeForm();

        try {
            this.formModel = this.editorService.parseCode(this.model.value); // parse code for every document type
        } catch (error: unknown) {
            await this.handleParsingErrors(error);
        }

        if (!this.formModel) { // empty or invalid field8 value (because model was not parsed)
            this.formModel = this.editorService.createDefaultModel();
        }
    }

    public $onDestroy() {
        this.subscription?.unsubscribe();
    }

    public getFormController(formController: FormController<AuthorityField8Model>) {
        this.formController = formController;
        this.subscription = this.formController.getFieldState$().subscribe((state) => {
            this.isFormValid = state.valid;
            this.$scope.$applyAsync();
        });
    }

    public submit() {
        if (!this.isFormValid || this.hasError) {
            return;
        }

        this.modalWindowActions.submitPromise(Promise.resolve(createPayloadActionResponse(this.editorService.serializeModel(this.formModel))));
    }

    public get hasError(): boolean {
        return !!this.error;
    }

    public getError(): Error {
        return this.error;
    }

    private async handleParsingErrors(error: unknown) {
        this.logService.error(error);
        if (error instanceof IncorrectSectionFormatError) {
            const errorMessage = `hodnota [${error.sectionValue}] sekce [${error.section}] má chybný formát`;
            await this.askUserToUseDefaults(errorMessage);
        } else if (error instanceof UnknownSectionsCodesError) {
            const errorMessage = `pro kódy ze sekcí [${error.sectionsWithUnknownCodes.join(', ')}] nebyla nalezena definovaná hodnota`;
            await this.askUserToUseDefaults(errorMessage);
        } else {
            throw new Error('Unknown field8 parsing error');
        }
    }

    private async askUserToUseDefaults(errorMessage: string) {
        await this.modalConversationService.confirmationDialog({text: `Pole 008 má chybnou strukturu (${errorMessage}). Chcete nastavit výchozí hodnoty?`})
        this.formModel = null;
    }
}